import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, BarChart3, Megaphone, Ticket, Grid3X3, ImageUp, MessageSquare,
  MapPin, Video, Share2, UtensilsCrossed, Coins, Users, Mail, Bell, Shield, FileText, Briefcase, UserCheck, Save, ShoppingBag, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { getAdminStats, verifyDashboardPin } from '../services/api';
import { toast } from '../hooks/use-toast';
import {
  DashboardStats,
  LoyaltyMembersTab,
  ContactsTab,
  MenuItemsTab,
  MerchandiseTab,
  AdminAccountsTab,
  NotificationsTab,
  SpecialsTab,
  GalleryTab,
  LocationsTab,
  VideosTab,
  SocialTab,
  EventsTab,
  GallerySubmissionsTab,
  SocialPostsTab,
  UsersTab,
  DJScheduleTab,
  SettingsTab,
  CareersTab,
  MariettaWaitlistTab,
  PeopleTab
} from '../components/admin';
import { PageContentTab } from '../components/admin';
import WeeklyVideosCard from '../components/admin/WeeklyVideosCard';
import KaraokeControlCard from '../components/admin/KaraokeControlCard';
import DJControlTab from '../components/admin/DJControlTab';
import { Radio, Settings, Music } from 'lucide-react';
// Main Admin Page Component
const AdminPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [pinOk, setPinOk] = useState(() => {
    try { return sessionStorage.getItem('dashboardPinOk') === 'true'; } catch { return false; }
  });
  const [pinInput, setPinInput] = useState('');
  const [pinSubmitting, setPinSubmitting] = useState(false);

  useEffect(() => {
    if (pinOk) fetchStats();
  }, [pinOk]);

  const fetchStats = async () => {
    try {
      const data = await getAdminStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  };

  const handlePinSubmit = async (e) => {
    e?.preventDefault();
    const pin = pinInput.trim();
    if (!pin) {
      toast({ title: 'PIN required', description: 'Enter your dashboard PIN', variant: 'destructive' });
      return;
    }
    setPinSubmitting(true);
    try {
      await verifyDashboardPin(pin);
      sessionStorage.setItem('dashboardPinOk', 'true');
      setPinOk(true);
      setPinInput('');
    } catch (err) {
      toast({ title: 'Access denied', description: err.message || 'Invalid PIN', variant: 'destructive' });
      setPinInput('');
    } finally {
      setPinSubmitting(false);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'specials', label: 'Post Special', icon: Megaphone },
    { id: 'events', label: 'Events', icon: Ticket },
    { id: 'careers', label: 'Careers', icon: Briefcase },
    { id: 'marietta', label: 'Marietta Waitlist', icon: MapPin },
    { id: 'dj-schedule', label: 'DJ Schedule', icon: Music },
    { id: 'dj-control', label: 'DJ Control', icon: Radio },
    { id: 'gallery', label: 'Gallery', icon: Grid3X3 },
    { id: 'submissions', label: 'Submissions', icon: ImageUp },
    { id: 'posts', label: 'Posts', icon: MessageSquare },
    { id: 'locations', label: 'Locations', icon: MapPin },
    { id: 'videos', label: 'Videos', icon: Video },
    { id: 'social', label: 'Social Links', icon: Share2 },
    { id: 'menu', label: 'Menu Items', icon: UtensilsCrossed },
    { id: 'merchandise', label: 'Merchandise', icon: ShoppingBag },
    { id: 'page-content', label: 'Page Content', icon: FileText },
    { id: 'users', label: 'Users', icon: Coins },
    { id: 'people', label: 'People', icon: UserCheck },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'admins', label: 'Admin Accounts', icon: Shield },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="admin-dashboard">
      {/* PIN gate — must pass before rendering any dashboard content */}
      {!pinOk ? (
        <div className="min-h-screen flex items-center justify-center p-4" data-testid="dashboard-pin-gate">
          <Card className="w-full max-w-sm bg-slate-900 border-slate-700">
            <CardContent className="p-6 text-center">
              <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-red-600/20 flex items-center justify-center">
                <Lock className="w-7 h-7 text-red-400" />
              </div>
              <h2 className="text-white text-xl font-bold mb-1">Dashboard Access</h2>
              <p className="text-slate-400 text-sm mb-5">Enter the PIN to continue.</p>
              <form onSubmit={handlePinSubmit} className="space-y-3">
                <Input
                  type="password"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  value={pinInput}
                  onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="••••"
                  autoFocus
                  className="bg-slate-800 border-slate-700 text-white text-center text-2xl tracking-[0.5em] h-14"
                  data-testid="dashboard-pin-input"
                />
                <Button
                  type="submit"
                  disabled={pinSubmitting || !pinInput}
                  className="w-full bg-red-600 hover:bg-red-700 h-11"
                  data-testid="dashboard-pin-submit"
                >
                  {pinSubmitting ? 'Verifying...' : 'Unlock'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      ) : (
      <>
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_57379523-4651-4150-aa1e-60b8df6a4f7c/artifacts/zzljit87_Untitled%20design.png" 
              alt="Fin & Feathers"
              className="h-10 cursor-pointer"
              onClick={() => navigate('/')}
            />
            <span className="text-white font-semibold">Admin Panel</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => {
                toast({ title: 'Saved', description: 'All changes are live on the homepage' });
                setTimeout(() => window.open('/', '_blank'), 500);
              }}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="dashboard-save-btn"
            >
              <Save className="w-4 h-4 mr-2" /> Save Changes
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="text-slate-300 hover:text-white"
            >
              <Home className="w-4 h-4 mr-2" /> Home
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id 
                ? 'bg-red-600 hover:bg-red-700 text-white' 
                : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && (
          <div>
            <DashboardStats stats={stats} />
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6">
                <h3 className="text-white font-semibold mb-4">Quick Actions</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button onClick={() => setActiveTab('specials')} className="bg-red-600 hover:bg-red-700">
                    <Megaphone className="w-4 h-4 mr-2" /> Post Special
                  </Button>
                  <Button onClick={() => setActiveTab('gallery')} className="bg-orange-600 hover:bg-orange-700">
                    <Grid3X3 className="w-4 h-4 mr-2" /> Manage Gallery
                  </Button>
                  <Button onClick={() => setActiveTab('menu')} className="bg-yellow-600 hover:bg-yellow-700">
                    <UtensilsCrossed className="w-4 h-4 mr-2" /> Manage Menu
                  </Button>
                  <Button onClick={() => setActiveTab('notifications')} className="bg-purple-600 hover:bg-purple-700">
                    <Bell className="w-4 h-4 mr-2" /> Send Notification
                  </Button>
                </div>
              </CardContent>
            </Card>
            <KaraokeControlCard />
          </div>
        )}

        {activeTab === 'specials' && <SpecialsTab />}
        {activeTab === 'specials' && <WeeklyVideosCard />}
        {activeTab === 'events' && <EventsTab />}
        {activeTab === 'careers' && <CareersTab />}
        {activeTab === 'marietta' && <MariettaWaitlistTab />}
        {activeTab === 'gallery' && <GalleryTab />}
        {activeTab === 'submissions' && <GallerySubmissionsTab />}
        {activeTab === 'posts' && <SocialPostsTab />}
        {activeTab === 'locations' && <LocationsTab />}
        {activeTab === 'videos' && <VideosTab />}
        {activeTab === 'social' && <SocialTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'people' && <PeopleTab />}
        {activeTab === 'menu' && <MenuItemsTab />}
        {activeTab === 'merchandise' && <MerchandiseTab />}
        {activeTab === 'page-content' && <PageContentTab />}
        {activeTab === 'notifications' && <NotificationsTab />}
        {activeTab === 'admins' && <AdminAccountsTab />}
        {activeTab === 'dj-schedule' && <DJScheduleTab />}
        {activeTab === 'dj-control' && <DJControlTab />}
        {activeTab === 'settings' && <SettingsTab />}
      </div>
      </>
      )}
    </div>
  );
};

export default AdminPage;
